'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  CheckCircle2,
  ScanLine,
  ClipboardList,
  Clock,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { useRouter } from '@/i18n/routing';
import { PrintPortal } from '@/components/print-portal';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageActionBar } from '@/components/layout/page-action-bar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { receptionQueries } from '../services/reception.queries';
import {
  ReceptionWizardHeader,
  TWO_STEP_WIZARD
} from './reception-wizard-header';
import type { ReceptionSession } from '../types';

const STEP_SUBTITLE_KEYS = [
  'wizardSubtitleDataVerification',
  'wizardSubtitleSummary'
] as const;

interface ReceptionDetailViewProps {
  shipmentId: number;
}

function computeViewStep(session: ReceptionSession): {
  maxStep: number;
  completedSteps: number[];
} {
  const completed: number[] = [];
  const items = session.items ?? [];
  const allMatched =
    items.length > 0 && items.every((i) => i.status === 'matched');

  if (session.status === 'completed') {
    completed.push(0, 1);
    return { maxStep: 1, completedSteps: completed };
  }
  if (allMatched) {
    completed.push(0);
    return { maxStep: 1, completedSteps: completed };
  }
  return { maxStep: 0, completedSteps: completed };
}

export function ReceptionDetailView({ shipmentId }: ReceptionDetailViewProps) {
  const t = useTranslations('reception');
  const router = useRouter();
  const [initialized, setInitialized] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const { data: shipmentResponse, isLoading } = useQuery(
    receptionQueries.detail(shipmentId)
  );

  const shipment = shipmentResponse?.data ?? null;
  const session: ReceptionSession | null =
    shipment?.active_session ?? shipment?.latest_session ?? null;

  const { data: summaryResponse } = useQuery({
    ...receptionQueries.summary(session?.id ?? 0),
    enabled: !!session?.id
  });
  const summary = summaryResponse?.data ?? null;

  const { maxStep, completedSteps } = session
    ? computeViewStep(session)
    : { maxStep: 0, completedSteps: [] as number[] };

  // Initialize current step from session progress once
  if (!initialized && session) {
    setCurrentStep(maxStep);
    setInitialized(true);
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='text-muted-foreground'>{t('loading')}</div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='text-muted-foreground'>{t('shipmentNotFound')}</div>
      </div>
    );
  }

  const items = session?.items ?? [];
  const matchedItems = items.filter((i) => i.status === 'matched').length;
  const totalItems = items.length;
  const completionPercentage =
    totalItems > 0 ? Math.round((matchedItems / totalItems) * 100) : 0;

  const canGoBack = currentStep > 0;
  const canGoNext = currentStep < maxStep;

  return (
    <div className='flex flex-1 flex-col gap-6'>
      <div className='flex flex-col items-center space-y-6'>
        {/* Top bar */}
        <div className='flex w-full items-start justify-between'>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>
              {t('pageTitle')}
            </h1>
            <p className='text-muted-foreground mt-1 text-sm'>
              {t('wizardStepLabel', { step: currentStep + 1 })}
              {' · '}
              {t(STEP_SUBTITLE_KEYS[currentStep])}
            </p>
          </div>
          <div className='flex items-center gap-3'>
            {session?.status === 'completed' ? (
              <Badge className='bg-green-500 text-white'>
                <CheckCircle2 className='me-1 h-3 w-3' />
                {t('completed')}
              </Badge>
            ) : session ? (
              <Badge
                variant='outline'
                className='border-primary/30 bg-primary/5 text-primary gap-1.5 px-3 py-1.5'
              >
                <span className='bg-primary h-2 w-2 rounded-full' />
                {t('sessionActive')}
              </Badge>
            ) : (
              <Badge variant='secondary'>{t('noSession')}</Badge>
            )}
          </div>
        </div>

        {/* Stepper */}
        <ReceptionWizardHeader
          currentStep={currentStep}
          completedSteps={completedSteps}
          steps={TWO_STEP_WIZARD}
        />

        {/* Step 0: Data Verification — read-only */}
        {currentStep === 0 && session && (
          <div className='container space-y-6'>
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <ScanLine className='h-5 w-5' />
                    {t('dataVerificationTitle')}
                  </div>
                  <Badge
                    variant={
                      matchedItems === totalItems && totalItems > 0
                        ? 'default'
                        : 'secondary'
                    }
                    className={cn(
                      matchedItems === totalItems &&
                        totalItems > 0 &&
                        'bg-green-500 text-white hover:bg-green-500/80'
                    )}
                  >
                    {completionPercentage}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='bg-muted h-2 w-full overflow-hidden rounded-full'>
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-300',
                      matchedItems === totalItems && totalItems > 0
                        ? 'bg-green-500'
                        : 'bg-primary'
                    )}
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>

                <p className='text-muted-foreground text-sm'>
                  {t('matchedItemsCount', {
                    matched: matchedItems,
                    total: totalItems
                  })}
                </p>

                {totalItems > 0 && (
                  <div className='rounded-md border'>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('containerIdentifier')}</TableHead>
                          <TableHead>{t('label')}</TableHead>
                          <TableHead>{t('status')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className='font-mono text-sm'>
                              {item.container_identifier}
                            </TableCell>
                            <TableCell>{item.label ?? '-'}</TableCell>
                            <TableCell>
                              {item.status === 'matched' ? (
                                <Badge
                                  variant='default'
                                  className='bg-green-500 text-white hover:bg-green-500/80'
                                >
                                  <CheckCircle2 className='me-1 h-3 w-3' />
                                  {t('statusMatched')}
                                </Badge>
                              ) : (
                                <Badge variant='secondary'>
                                  <Clock className='me-1 h-3 w-3' />
                                  {t('statusPending')}
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {totalItems === 0 && (
                  <div
                    className={cn(
                      'flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-gray-500',
                      'dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400'
                    )}
                  >
                    <Clock className='h-5 w-5 shrink-0' />
                    <span className='text-sm font-medium'>
                      {t('statusPending')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 1: Summary — read-only */}
        {currentStep === 1 && session && summary && (
          <div className='container max-w-2xl space-y-6'>
            {/* Receipt-style card */}
            <div className='bg-card rounded-lg border shadow-sm'>
              {/* Header */}
              <div className='border-b px-6 py-5 text-center'>
                <ClipboardList className='text-primary mx-auto mb-2 h-8 w-8' />
                <h2 className='text-xl font-semibold'>{t('summaryTitle')}</h2>
                <p className='text-muted-foreground mt-1 text-sm'>
                  {t('summarySubtitle')}
                </p>
              </div>

              {/* Rows */}
              <div className='divide-y'>
                {[
                  {
                    label: t('shipmentNumber'),
                    value: summary.shipment_number
                  },
                  // {
                  //   label: t('client'),
                  //   value: summary.judicial_entity
                  // },
                  {
                    label: t('qrVerification'),
                    value: summary.qr_verified
                      ? t('matchedToManifest')
                      : t('notVerified'),
                    badge: summary.qr_verified
                  },
                  {
                    label: t('expectedBoxes'),
                    value: summary.expected_boxes
                  },
                  {
                    label: t('actualCount'),
                    value: summary.actual_boxes ?? t('notAvailable')
                  },
                  {
                    label: t('countStatus'),
                    value:
                      summary.count_status === 'matched'
                        ? t('countVerifiedBadge')
                        : (summary.count_status ?? t('notAvailable')),
                    badge: summary.count_status === 'matched'
                  },
                  {
                    label: t('matchingManifestItems'),
                    value: `${summary.matched_items} / ${summary.total_items}`
                  },
                  {
                    label: t('reportedDiscrepancies'),
                    value: summary.discrepancy_notes ?? t('none')
                  },
                  {
                    label: t('finalStatus'),
                    value: summary.is_complete
                      ? t('receptionComplete')
                      : t('receptionIncomplete'),
                    badge: summary.is_complete
                  }
                ].map((row) => (
                  <div
                    key={row.label}
                    className='flex items-center justify-between px-6 py-4'
                  >
                    <span className='text-muted-foreground text-sm'>
                      {row.label}
                    </span>
                    {row.badge ? (
                      <Badge
                        className={cn(
                          'gap-1 bg-green-500 text-white hover:bg-green-500/80'
                        )}
                      >
                        <CheckCircle2 className='h-3 w-3' />
                        {row.value}
                      </Badge>
                    ) : (
                      <span className='font-medium'>{row.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Print-only summary */}
        {session && summary && (
          <PrintPortal>
            <div className='hidden' data-print-zone>
              <div className='space-y-8 bg-white p-8 text-black'>
                {/* Print Header */}
                <div className='flex items-center justify-between border-b pb-4'>
                  <div>
                    <h1 className='text-2xl font-bold'>Safha Archive</h1>
                    <p className='text-sm text-gray-500'>
                      Reception Summary Report
                    </p>
                  </div>
                  <div className='text-right'>
                    <p className='text-sm font-medium'>
                      {new Date().toLocaleString()}
                    </p>
                    <p className='text-xs text-gray-500'>
                      Session ID: {session.id}
                    </p>
                  </div>
                </div>

                {/* Main Info */}
                <div className='grid grid-cols-2 gap-x-12 gap-y-4'>
                  <div>
                    <p className='mb-1 text-xs font-bold text-gray-500 uppercase'>
                      {t('shipmentNumber')}
                    </p>
                    <p className='text-lg font-semibold'>
                      {summary.shipment_number}
                    </p>
                  </div>
                  {/* <div>
                    <p className='mb-1 text-xs font-bold text-gray-500 uppercase'>
                      {t('client')}
                    </p>
                    <p className='text-lg font-semibold'>
                      {summary.judicial_entity}
                    </p>
                  </div> */}
                </div>

                {/* Verification Details Table */}
                <div className='overflow-hidden rounded-lg border'>
                  <table className='w-full text-sm'>
                    <thead className='border-b bg-gray-50'>
                      <tr>
                        <th className='px-4 py-3 text-left font-bold text-gray-700'>
                          {t('label')}
                        </th>
                        <th className='px-4 py-3 text-right font-bold text-gray-700'>
                          {t('verificationStatus')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y'>
                      {[
                        {
                          label: t('shipmentNumber'),
                          value: summary.shipment_number
                        },
                        // {
                        //   label: t('client'),
                        //   value: summary.judicial_entity
                        // },
                        {
                          label: t('qrVerification'),
                          value: summary.qr_verified
                            ? t('matchedToManifest')
                            : t('notVerified')
                        },
                        {
                          label: t('expectedBoxes'),
                          value: summary.expected_boxes
                        },
                        // {
                        //   label: t('actualCount'),
                        //   value: summary.actual_boxes ?? t('notAvailable')
                        // },
                        // {
                        //   label: t('countStatus'),
                        //   value:
                        //     summary.count_status === 'matched'
                        //       ? t('countVerifiedBadge')
                        //       : (summary.count_status ?? t('notAvailable'))
                        // },
                        // {
                        //   label: t('matchingManifestItems'),
                        //   value: `${summary.matched_items} / ${summary.total_items}`
                        // },
                        {
                          label: t('finalStatus'),
                          value: summary.is_complete
                            ? t('receptionComplete')
                            : t('receptionIncomplete')
                        }
                      ].map((row) => (
                        <tr key={row.label}>
                          <td className='px-4 py-3 text-gray-600'>
                            {row.label}
                          </td>
                          <td className='px-4 py-3 text-right font-medium'>
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Boxes & Containers Section */}
                {shipment.boxes && shipment.boxes.length > 0 && (
                  <div className='space-y-3'>
                    <h3 className='border-b pb-1 font-bold'>
                      {t('printBoxesTitle')}
                    </h3>
                    {shipment.boxes.map((box) => {
                      const registrations = box.registrations ?? [];
                      return (
                        <div
                          key={box.id}
                          className='overflow-hidden rounded-lg border'
                        >
                          <div className='flex items-center justify-between border-b bg-gray-50 px-4 py-2'>
                            <span className='text-sm font-bold'>
                              {t('printBoxNumber')} {box.box_number}
                            </span>
                            <span className='text-xs text-gray-500'>
                              {box.status}
                            </span>
                          </div>
                          {registrations.length > 0 ? (
                            <table className='w-full text-sm'>
                              <thead className='border-b bg-white'>
                                <tr>
                                  <th className='px-4 py-2 text-start font-medium text-gray-600'>
                                    {t('printContainerNumber')}
                                  </th>
                                  <th className='px-4 py-2 text-end font-medium text-gray-600'>
                                    {t('printContainerType')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='divide-y'>
                                {registrations.map((reg) => (
                                  <tr key={reg.id}>
                                    <td className='px-4 py-2 font-mono text-xs'>
                                      {reg.container_number}
                                    </td>
                                    <td className='px-4 py-2 text-end text-xs'>
                                      {reg.container_type?.name ?? '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className='px-4 py-3 text-xs text-gray-400'>
                              {t('printNoContainersForBox')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Discrepancies Section */}
                <div className='space-y-2'>
                  <h3 className='border-b pb-1 font-bold'>
                    {t('reportedDiscrepancies')}
                  </h3>
                  <p className='min-h-15 rounded border bg-gray-50 p-4 text-sm'>
                    {summary.discrepancy_notes || t('noDiscrepancies')}
                  </p>
                </div>

                {/* Signature Section */}
                <div className='grid grid-cols-2 gap-12 pt-12'>
                  <div className='border-t pt-2'>
                    <p className='mb-8 text-xs font-bold text-gray-400 uppercase'>
                      Received By
                    </p>
                    <div className='mb-1 w-full border-b border-gray-300'></div>
                    <p className='text-xs text-gray-500'>Name & Signature</p>
                  </div>
                  <div className='border-t pt-2'>
                    <p className='mb-8 text-xs font-bold text-gray-400 uppercase'>
                      Authorized By
                    </p>
                    <div className='mb-1 w-full border-b border-gray-300'></div>
                    <p className='text-xs text-gray-500'>Name & Signature</p>
                  </div>
                </div>

                {/* Print Footer */}
                <div className='pt-8 text-center text-[10px] tracking-widest text-gray-400 uppercase'>
                  Generated by Safha Archive Management System
                </div>
              </div>
            </div>
          </PrintPortal>
        )}
      </div>

      <PageActionBar
        start={
          <Button
            variant='ghost'
            size='sm'
            onClick={() => router.push('/dashboard/shipment-statement')}
          >
            <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
            {t('backToListing')}
          </Button>
        }
      >
        <Button
          variant='outline'
          size='sm'
          onClick={() => setCurrentStep((s) => s - 1)}
          disabled={!canGoBack}
        >
          <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
          {t('back')}
        </Button>
        <Button
          size='sm'
          onClick={() => setCurrentStep((s) => s + 1)}
          disabled={!canGoNext}
        >
          {t('viewSummary')}
          <ArrowRight className='ms-2 h-4 w-4 rtl:rotate-180' />
        </Button>
      </PageActionBar>
    </div>
  );
}
